/**
 * Multi-unit Power Distribution Summary display (#997 / #996 / Story 4.4) —
 * descriptor-engine-driven rows via a thin shim so Summary consumers'
 * `getPerPowerDistributionSummaryDefinitions` /
 * `powerDistributionSummaryUnitRowValue` imports are undisturbed.
 */

import {
  POWER_DISTRIBUTION_EDITOR_SUBSECTION_FIELDS,
  POWER_DISTRIBUTION_PER_INSTANCE_EDITOR_FIELD_KEYS,
} from '../request/fields/requestFieldRegistry';
import {
  getPowerDistributionDescriptorSummaryDefinitions,
  getPowerDistributionDescriptorUnitTitle,
  powerDistributionDescriptorUnitRowValue,
} from '../request/sections/powerDistributionDescriptorSummary';
import { countSubsectionFields } from '../utils/subsectionHeaderMetrics';

import type { SummaryCardRow } from './summaryCardSourceTypes';
import type { PowerDistributionConfiguration } from '../types/powerDistribution';

export {
  POWER_DISTRIBUTION_EDITOR_SUBSECTION_FIELDS,
  POWER_DISTRIBUTION_PER_INSTANCE_EDITOR_FIELD_KEYS as POWER_DISTRIBUTION_PER_INSTANCE_FIELD_KEYS,
};

/**
 * Per-unit Summary rows for one Power Distribution — engine-driven source
 * (Story 4.4). Byte-identical to the pre-engine bespoke row set (parity
 * oracle `power-distribution-summary-rows.json`).
 */
export function getPerPowerDistributionSummaryDefinitions(): SummaryCardRow[] {
  return getPowerDistributionDescriptorSummaryDefinitions();
}

export function resolveSummaryPowerDistributionConfigurations(input: {
  sessionUnits?: PowerDistributionConfiguration[] | null;
  draftUnits?: PowerDistributionConfiguration[] | null;
  persistedUnits?: PowerDistributionConfiguration[] | null;
  /** #997 C3: true when edited this session — an empty edited array is authoritative (no delete-all resurrection). */
  edited?: boolean;
}): PowerDistributionConfiguration[] {
  const { sessionUnits, draftUnits, persistedUnits, edited } = input;
  if (sessionUnits && sessionUnits.length > 0) {
    return sessionUnits;
  }
  if (edited) {
    return draftUnits ?? [];
  }
  if (draftUnits && draftUnits.length > 0) {
    return draftUnits;
  }
  if (persistedUnits && persistedUnits.length > 0) {
    return persistedUnits;
  }
  return [];
}

export function powerDistributionSummaryUnitRowValue(
  unit: PowerDistributionConfiguration,
  dataField: string,
): string | number | boolean | null | undefined {
  return powerDistributionDescriptorUnitRowValue(unit, dataField);
}

/**
 * Per-unit Summary card title — descriptor-driven (mirrors the LU shim
 * `getLargeUpsSummaryUnitTitle` and the Racks shim
 * `getRackSummaryUnitTitle`). Story 4.4 golden parity oracle: the Summary
 * golden test pins the production title output, not a local helper.
 */
export function getPowerDistributionSummaryUnitTitle(
  unit: PowerDistributionConfiguration,
  index: number,
): string {
  return getPowerDistributionDescriptorUnitTitle(unit, index);
}

export function countFilledPowerDistributionSummaryFields(
  unit: PowerDistributionConfiguration,
  rows?: readonly SummaryCardRow[],
): number {
  const fieldKeys = new Set(
    (rows ?? getPerPowerDistributionSummaryDefinitions()).map(
      row => row.dataField,
    ),
  );
  const fields = POWER_DISTRIBUTION_EDITOR_SUBSECTION_FIELDS.filter(field =>
    fieldKeys.has(field.key),
  );
  return countSubsectionFields(
    fields,
    unit as unknown as Record<string, unknown>,
  ).filled;
}

export function countFilledPowerDistributionSummaryFieldsForUnits(
  units: readonly PowerDistributionConfiguration[],
  rows: readonly SummaryCardRow[],
): number {
  return units.reduce(
    (sum, unit) => sum + countFilledPowerDistributionSummaryFields(unit, rows),
    0,
  );
}
